import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  loginSchema,
  type LoginInput,
} from "../../domain/validation/loginSchema";
import { useLogin } from "../../application/hooks/useLogin";
import { IconButton, InputAdornment, Alert } from "@mui/material";
import AuthPrimaryButton from "./AuthPrimaryButton";
import AuthTextField from "./AuthTextField";
import { Link, useNavigate } from "react-router-dom";
import { VisibilityOff, Visibility } from "@mui/icons-material";
import { authTextButtonStyle } from "../theme/authTextButtonStyle";

const LoginForm = () => {
  const { t } = useTranslation("auth");
  const { login, loading } = useLogin();
  const navigate = useNavigate();

  const [form, setForm] = useState<LoginInput>({
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState<Partial<LoginInput>>({});
  const [error, setError] = useState("");
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [showPassword, setShowPassword] = React.useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const getValidationErrors = (input: LoginInput): Partial<LoginInput> => {
    const result = loginSchema.safeParse(input);

    if (result.success) {
      return {};
    }

    const fieldErrors: Partial<LoginInput> = {};
    result.error.issues.forEach((err) => {
      const field = err.path[0] as keyof LoginInput;
      fieldErrors[field] = t(err.message);
    });

    return fieldErrors;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updatedForm = { ...form, [e.target.name]: e.target.value };
    setForm(updatedForm);

    if (hasSubmitted) {
      setErrors(getValidationErrors(updatedForm));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasSubmitted(true);
    setError("");

    const result = loginSchema.safeParse(form);

    if (!result.success) {
      const fieldErrors: Partial<LoginInput> = {};
      result.error.issues.forEach((err) => {
        const field = err.path[0] as keyof LoginInput;
        fieldErrors[field] = t(err.message);
      });
      setErrors(fieldErrors);
      return;
    }

    setErrors({});
    try {
      const loginResult = await login(result.data);

      // Flow decision moved to application layer - use-case returns explicit next step
      switch (loginResult.nextStep) {
        case "setup-password":
          navigate("/setup-password");
          break;
        case "setup-twofa":
          navigate("/setup-twofa");
          break;
        case "verification-code":
          navigate("/verification-code", { state: { email: form.email } });
          break;
        case "email-verification":
          navigate("/verify-email-otp", { state: { email: form.email } });
          break;
        case "complete":
          navigate("/patients");
          break;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      const message = err.data?.message || err.message || t("login.genericError");
      setError(message);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex w-full flex-col gap-6">
      {error && setTimeout(() => setError(""), 5000) && (
        <Alert severity="error">{error}</Alert>
      )}
      {/* Email */}
      <div>
        <AuthTextField
          name="email"
          label={t("login.emailLabel")}
          type="email"
          placeholder={t("login.emailPlaceholder")}
          value={form.email}
          onChange={handleChange}
          error={!!errors.email}
          helperText={errors.email}
          className="w-full"
        />
      </div>

      {/* Password */}
      <div className="flex flex-col">
        <AuthTextField
          name="password"
          label={t("login.passwordLabel")}
          type={showPassword ? "text" : "password"}
          placeholder="*******"
          value={form.password}
          onChange={handleChange}
          error={!!errors.password}
          helperText={errors.password}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={
                    showPassword ? t("login.showPassword") : t("login.hidePassword")
                  }
                  onClick={handleClickShowPassword}
                  edge="end"
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Link
          to="/forgot-password"
          className="mt-2 self-end text-primary"
          style={authTextButtonStyle}
        >
          {t("login.forgotPassword")}
        </Link>
      </div>

      <AuthPrimaryButton type="submit" loading={loading}>
        {t("login.submit")}
      </AuthPrimaryButton>

      <div className="flex flex-wrap items-baseline justify-center text-center text-sm leading-[var(--type-button-line-height)] text-gray-600">
        {t("login.noAccount")}&nbsp;
        <Link
          to="/signup"
          className="text-primary"
          style={authTextButtonStyle}
        >
          {t("login.registerNow")}
        </Link>
      </div>
    </form>
  );
};

export default LoginForm;
